import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import {
  LocationSelectionDialog,
  useLocationSelection,
} from 'src/components/LocationSelectionDialog';
import { Stack } from 'src/components/Stack';
import { useSession } from 'src/contexts/SessionContext';
import { useBarcodeScanner } from 'src/hooks/useBarcodeScanner';
import { Cart, CartItem as CartItemType } from 'src/models/cart/_types';
import { searchCartItem } from 'src/models/cart/cart';
import { Location } from 'src/models/location/_types';
import { fetchLocations } from 'src/models/location/location';
import { Product } from 'src/models/product/_types';
import { fetchProduct, fetchProductVariants } from 'src/models/product/product';
import { isVariantOutOfStock } from 'src/models/product/utils';
import { Trainer } from 'src/models/trainer/trainer';
import { User } from 'src/models/user/_types';
import { PurchaseProductDialog } from 'src/pages/Purchase/components/PurchaseProductDialog';
import { selectableItemTypes } from 'src/shared/constants/itemTypes';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';
import { Identifier, WithSearchResult } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import ItemFilters from '../../pages/Clients/PaymentDialog/ItemFilters';
import { SearchAndAddItem } from '../../pages/Clients/PaymentDialog/SearchAndAddItem';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ItemBrowserProps<T extends { id: Identifier }> {
  user?: User;
  selectedItemType?: string | null;
  searchValue: string;
  onSearchChange: (value: string) => void;
  debouncedSearchValue: string;
  onSelect: (item: any, options?: any) => Promise<void>;
  onRemove: (item: CartItemType, index?: number) => void;
  selectedItems: CartItemType[]; // More generic name for selected items (cart items, selected packages, etc.)
  includeOnlineTraining?: boolean;
  checkIfItemAdded?: (item: SearchItem) => boolean; // Optional - defaults to ID-based check
  trainer?: Trainer;
  cart: Cart | null;
  updateLocationId?: (locationId: Identifier) => void;
  fromPosConfig?: boolean;
}

interface SearchItem extends CartItemType {
  id: Identifier;
  name: string;
  aliases?: string[];
  header?: string;
  type: string;
}

export const ItemBrowser = <T extends { id: Identifier }>({
  user,
  selectedItemType: selectedItemTypeValue,
  searchValue,
  onSearchChange,
  debouncedSearchValue,
  onSelect,
  onRemove,
  selectedItems,
  includeOnlineTraining = false,
  checkIfItemAdded,
  trainer,
  cart,
  updateLocationId,
  fromPosConfig = false,
}: ItemBrowserProps<T>) => {
  // Default implementation: check if item ID matches any selected item ID
  const defaultCheckIfItemAdded = (item: SearchItem) => {
    return selectedItems.some((selectedItem) => selectedItem.id === item.id);
  };

  const actualCheckIfItemAdded = checkIfItemAdded || defaultCheckIfItemAdded;
  const { canManageAll } = usePermissionCheck();
  const { t } = useTranslation('translation', {
    keyPrefix: 'common.itemTypes',
  });
  const { t: tPayment } = useTranslation('translation', {
    keyPrefix: 'client.payment',
  });
  const { session } = useSession();

  // State for managing the product dialog
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  // State for managing location selection dialog
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [pendingProductForLocation, setPendingProductForLocation] =
    useState<Product | null>(null);

  // State for barcode scanning and out-of-stock confirmation
  const [confirmDialogProps, confirmOutOfStock] = useConfirmDialogComponent();

  // State for barcode scanning with pending location selection
  const [pendingBarcodeVariant, setPendingBarcodeVariant] = useState<any>(null);

  // Use location selection hook
  const { handleLocationSelect: handleLocationSelectBase } =
    useLocationSelection();

  // Fetch locations to determine if dialog should be shown
  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: () => fetchLocations({ fetchAll: true }),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const {
    filterParams,
    resetFilterParams,
    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
  } = useListSearchParams({
    paramPrefix: 'p_',
  });

  // Find the selected ItemType object from the string value
  const selectedItemType = selectedItemTypeValue
    ? selectableItemTypes.find(
        (type) => type.value === selectedItemTypeValue
      ) || null
    : null;

  const getHeaderForType = useCallback(
    (type: string) => {
      switch (type) {
        case 'onlineTraining':
          return t('onlineTraining');
        case 'group':
          return t('groups');
        case 'package':
          return t('packages');
        case 'product':
          return t('products');
        case 'plan':
          return t('plans');
        default:
          return type.charAt(0).toUpperCase() + type.slice(1) + 's';
      }
    },
    [t]
  );

  // Helper function to add a barcode variant after location is confirmed
  const handleBarcodeVariantAdd = useCallback(
    async (matchedVariant: any, locationId: Identifier | null) => {
      try {
        // Check if the variant is out of stock (only if locationId is provided)
        const isOutOfStock = locationId
          ? isVariantOutOfStock(matchedVariant, locationId, cart)
          : false;

        if (isOutOfStock) {
          if (canManageAll) {
            // Show confirmation dialog for users with canManageAll permission
            const confirmed = await confirmOutOfStock({
              title: tPayment('outOfStockTitle'),
              message: tPayment('outOfStockMessage', {
                productName:
                  matchedVariant.displayName ||
                  matchedVariant.product?.name ||
                  tPayment('product'),
              }),
              confirmText: tPayment('addAnywayButton'),
              cancelText: tPayment('cancelButton'),
              confirmColor: 'error',
            });

            if (!confirmed) {
              return;
            }
          } else {
            // Show error toast for users without canManageAll permission
            toast.error(tPayment('outOfStockError'));
            return;
          }
        }

        // Convert the variant to the expected format for onSelect
        const variantAsSearchResult = {
          ...matchedVariant,
          type: 'product-variant',
          header: getHeaderForType('product'),
          item: matchedVariant,
          name:
            matchedVariant.displayName ||
            matchedVariant.product?.name ||
            tPayment('productVariant'),
        } as WithSearchResult<T>;

        // Add the variant to the cart
        await onSelect(variantAsSearchResult);

        // Show success toast
        toast.success(
          tPayment('itemAddedByBarcode', {
            productName:
              matchedVariant.displayName ||
              matchedVariant.product?.name ||
              tPayment('product'),
          })
        );
      } catch (error) {
        // Log error for debugging but don't break the user experience
        toast.error(api.formatError(error, tPayment('barcodeSearchError')));
      }
    },
    [
      cart,
      canManageAll,
      confirmOutOfStock,
      getHeaderForType,
      onSelect,
      tPayment,
    ]
  );

  // Helper function to handle location-dependent actions based on available locations
  const handleLocationDependentAction = useCallback(
    (product?: Product, barcodeVariant?: any) => {
      // If from POS config, skip location selection entirely
      if (fromPosConfig) {
        if (product) {
          setSelectedProduct(product);
          setProductDialogOpen(true);
        }
        if (barcodeVariant) {
          handleBarcodeVariantAdd(barcodeVariant, null);
        }
        return;
      }

      const availableLocations = locationsData?.locations || [];

      // No locations available - proceed without location selection
      if (availableLocations.length === 0) {
        if (product) {
          setSelectedProduct(product);
          setProductDialogOpen(true);
        }
        if (barcodeVariant) {
          handleBarcodeVariantAdd(barcodeVariant, null);
        }
        return;
      }

      // Single location available - auto-select it
      if (availableLocations.length === 1) {
        const location = availableLocations[0];
        updateLocationId?.(location.id);

        if (product) {
          setSelectedProduct(product);
          setProductDialogOpen(true);
        }
        if (barcodeVariant) {
          handleBarcodeVariantAdd(barcodeVariant, location.id);
        }
        return;
      }

      // Multiple locations available - show dialog for selection
      if (product) {
        setPendingProductForLocation(product);
      }
      if (barcodeVariant) {
        setPendingBarcodeVariant(barcodeVariant);
      }
      setLocationDialogOpen(true);
    },
    [
      fromPosConfig,
      locationsData?.locations,
      updateLocationId,
      handleBarcodeVariantAdd,
    ]
  );

  // Barcode scanning function
  const handleBarcodeScanned = useCallback(
    async (barcode: string) => {
      try {
        // Search for product variants with the scanned barcode
        const { productVariants } = await fetchProductVariants({
          search: barcode,
          fetchAll: true,
        });

        // Find the exact barcode match
        const matchedVariant = productVariants.find(
          (variant) => variant.barcode === barcode
        );

        if (!matchedVariant) {
          toast.error(tPayment('barcodeNotFound'));
          return;
        }

        // If from POS config, skip location requirements entirely
        if (fromPosConfig) {
          await handleBarcodeVariantAdd(matchedVariant, null);
          return;
        }

        // Check if cart has locationId before proceeding
        if (!cart?.locationId) {
          // Use the location-dependent action handler
          handleLocationDependentAction(undefined, matchedVariant);
          return;
        }

        // Cart has locationId, proceed with adding the variant
        await handleBarcodeVariantAdd(matchedVariant, cart.locationId);
      } catch (error) {
        // Log error for debugging but don't break the user experience
        toast.error(api.formatError(error, tPayment('barcodeSearchError')));
      }
    },
    [
      cart?.locationId,
      fromPosConfig,
      handleBarcodeVariantAdd,
      handleLocationDependentAction,
      tPayment,
    ]
  );

  // Enable barcode scanning
  useBarcodeScanner({
    onScan: handleBarcodeScanned,
    minLength: 4,
    enabled: true,
  });

  const activeFilters =
    searchValue ||
    filterParams.paymentPackageTags ||
    filterParams.paymentProductTag ||
    filterParams.packageLocation;

  const resetFilters = () => {
    onSearchChange('');
    resetFilterParams();
  };

  // Handlers for product dialog
  const handleProductDialogClose = useCallback(() => {
    setProductDialogOpen(false);
    setSelectedProduct(null);
  }, []);

  const handleProductDialogAddToBag = useCallback(
    (cart: any, product: Product, selectedVariant?: any) => {
      if (product && selectedVariant) {
        // Close the dialog and call onSelect with the selected variant
        setProductDialogOpen(false);
        // Convert the variant to the expected format for onSelect
        const variantAsSearchResult = {
          ...selectedVariant,
          type: 'product-variant',
          header: getHeaderForType('product'),
          item: selectedVariant,
        } as WithSearchResult<T>;
        onSelect(variantAsSearchResult);
        setSelectedProduct(null);
      } else if (product && fromPosConfig) {
        const variantAsSearchResult = {
          ...product,
          type: 'product',
          header: getHeaderForType('product'),
          item: product,
        } as WithSearchResult<T>;

        onSelect(variantAsSearchResult);
        setProductDialogOpen(false);
      }

      setSelectedProduct(null);
    },
    [onSelect, getHeaderForType, fromPosConfig]
  );

  // Handler for when no locations are available (fallback for dialog component)
  const handleNoLocationsAvailable = useCallback(() => {
    setLocationDialogOpen(false);

    // If there's a pending product, open the product dialog without location
    if (pendingProductForLocation) {
      setSelectedProduct(pendingProductForLocation);
      setProductDialogOpen(true);
      setPendingProductForLocation(null);
    }

    // If there's a pending barcode variant, process it without location
    if (pendingBarcodeVariant) {
      handleBarcodeVariantAdd(pendingBarcodeVariant, null);
      setPendingBarcodeVariant(null);
    }
  }, [
    pendingProductForLocation,
    pendingBarcodeVariant,
    handleBarcodeVariantAdd,
  ]);

  // Handlers for location selection
  const handleLocationSelect = useCallback(
    (location: Location) => {
      handleLocationSelectBase(location, updateLocationId);
      setLocationDialogOpen(false);

      // If there's a pending product, open the product dialog now
      if (pendingProductForLocation) {
        updateLocationId?.(location.id);
        setSelectedProduct(pendingProductForLocation);
        setProductDialogOpen(true);
        setPendingProductForLocation(null);
      }

      // If there's a pending barcode variant, process it now that we have a location
      if (pendingBarcodeVariant) {
        updateLocationId?.(location.id);
        handleBarcodeVariantAdd(pendingBarcodeVariant, location.id);
        setPendingBarcodeVariant(null);
      }
    },
    [
      updateLocationId,
      pendingProductForLocation,
      pendingBarcodeVariant,
      handleLocationSelectBase,
      handleBarcodeVariantAdd,
    ]
  );

  const handleLocationDialogClose = useCallback(() => {
    // Always allow closing and clean up any pending state
    setLocationDialogOpen(false);
    setPendingProductForLocation(null);
    setPendingBarcodeVariant(null);
  }, []);

  // Enhanced onSelect handler that checks for products with variants
  const handleItemSelect = useCallback(
    async (item: WithSearchResult<T>) => {
      // Type assertion to access the properties we know exist on search results
      const searchItem = item as any;

      // Check if this is a product with variants
      if (searchItem.type === 'product') {
        const product = await fetchProduct({ id: searchItem.id });

        if (!product.productVariants || product.productVariants.length === 0) {
          // Open the product dialog instead of directly selecting
          onSelect(item);
        } else {
          // If from POS config, skip location requirements entirely
          if (fromPosConfig) {
            setSelectedProduct(product);
            setProductDialogOpen(true);
          } else {
            // Check if cart has locationId before opening product dialog
            if (!cart?.locationId) {
              // Use the location-dependent action handler
              handleLocationDependentAction(product, undefined);
            } else {
              // Cart has locationId, proceed with opening product dialog
              setSelectedProduct(product);
              setProductDialogOpen(true);
            }
          }
        }
      } else {
        // For non-products or products without variants, use the original behavior
        onSelect(item);
      }
    },
    [onSelect, cart?.locationId, fromPosConfig, handleLocationDependentAction]
  );

  const { data: searchResults = [], isLoading } = useQuery({
    queryKey: [
      'cartSearch',
      debouncedSearchValue,
      selectedItemType?.value,
      user?.id,
      filterParams.productTag,
      filterParams.packageLocation,
      filterParams.packageCategory,
      filterParams.services,
      filterParams.staff,
      !user, // Include user presence in cache key for digital filtering
    ],
    queryFn: async () => {
      const results = await searchCartItem({
        search: debouncedSearchValue,
        types: selectedItemType?.value ? [selectedItemType.value] : ['product'],
        params: {
          publishing_channel: 'staff',
          exclude_purchased_user_id: user?.id,
          package_tags: filterParams.packageCategory || null,
          product_tags: filterParams.productTag || null,
          location_ids: filterParams.packageLocation
            ? [filterParams.packageLocation]
            : null,
          service_ids: filterParams.services || null,
          trainer_ids: filterParams.staff || null,
          // Exclude digital products when no user is selected
          no_user: !user,
        },
      });

      // Create the online training item
      const onlineTrainingItem = {
        id: user?.trainerAssociationId || '',
        name: tPayment('onlineTraining'),
        itemId: user?.trainerAssociationId || '',
        itemType: 'onlineTraining',
        type: 'onlineTraining',
        item: {
          id: user?.trainerAssociationId || '',
          type: 'client',
          paymentOptions: session?.paymentOptions || [],
          paymentOption: session?.paymentOptions?.[0],
          imageUrl: '',
          name: tPayment('onlineTraining'),
          item: {
            id: user?.trainerAssociationId || '',
            type: 'client',
            name: tPayment('onlineTraining'),
          } as any,
        },
        quantity: 1,
        aliases: [
          tPayment('searchAliases.onlineTraining'),
          tPayment('searchAliases.trainingSubscription'),
        ],
        header: tPayment('onlineTraining'),
      } as SearchItem;

      const trainerId = trainer?.id || '';

      // Create the staff subscription item
      const staffSubscriptionItem = {
        id: trainerId,
        name: tPayment('staffSubscription'),
        itemId: trainerId,
        itemType: 'staffSubscription',
        type: 'staffSubscription',
        item: {
          id: trainerId,
          type: 'trainer',
          paymentOptions: session?.paymentOptions || [],
          paymentOption: session?.paymentOptions?.[0],
          imageUrl: '',
          name: tPayment('staffSubscription'),
          item: {
            id: trainerId,
            type: 'trainer',
            name: tPayment('staffSubscription'),
          } as any,
        },
        quantity: 1,
        aliases: ['staff subscription'],
        header: tPayment('staffSubscription'),
      } as SearchItem;

      // Add online training to results if:
      // 1. includeOnlineTraining is true
      // 2. Online Training type is specifically selected
      // 3. Search term is empty or matches "online training"
      const shouldIncludeOnlineTraining =
        includeOnlineTraining &&
        (selectedItemType?.value === 'onlineTraining' ||
          (selectedItemType?.value === null &&
            (!debouncedSearchValue ||
              `${tPayment('searchAliases.onlineTraining')} ${tPayment(
                'searchAliases.trainingSubscription'
              )}`.includes(debouncedSearchValue.toLowerCase()))));

      const shouldIncludeStaffSubscription =
        selectedItemType?.value === 'staffSubscription';

      const mappedResults = results.map((item) => ({
        ...item,
        id: item.id,
        name: item.name || '',
        aliases: item.aliases || [],
        header: getHeaderForType(item.type),
        type: item.type,
      }));

      return shouldIncludeOnlineTraining
        ? [onlineTrainingItem, ...mappedResults]
        : shouldIncludeStaffSubscription
          ? [staffSubscriptionItem, ...mappedResults]
          : mappedResults || [];
    },
    staleTime: 10000, // Consider results fresh for 10 seconds
    gcTime: 10000, // Cache for 10 seconds
  });

  return (
    <>
      <Stack spacing={2}>
        <ItemFilters
          resetFilterParams={resetFilterParams}
          params={params}
          updateParam={updateParam}
          updateFilterParam={updateFilterParam}
          getFilterParamValue={getFilterParamValue}
          selectedItemType={selectedItemTypeValue}
        />
        <SearchAndAddItem
          onAdd={(item) => handleItemSelect(item as WithSearchResult<T>)}
          onRemove={onRemove}
          itemList={selectedItems as CartItemType[]}
          isLoading={isLoading}
          searchResults={searchResults}
          activeFilters={activeFilters}
          resetFilters={resetFilters}
          selectedItemType={selectedItemType}
          selectedItems={selectedItems}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          checkIfItemAdded={actualCheckIfItemAdded}
        />
      </Stack>

      {/* Product Dialog for products with variants */}
      {selectedProduct && (
        <PurchaseProductDialog
          open={productDialogOpen}
          onClose={handleProductDialogClose}
          product={selectedProduct}
          onAddToBag={handleProductDialogAddToBag}
          cart={cart}
          locationId={cart?.locationId}
          addToCart={onSelect}
          publishingChannel="staff"
          canManageAll={canManageAll}
          fromPosConfig={fromPosConfig}
        />
      )}

      {/* Location Selection Dialog */}
      <LocationSelectionDialog
        open={locationDialogOpen}
        onClose={handleLocationDialogClose}
        onLocationSelect={handleLocationSelect}
        onNoLocationsAvailable={handleNoLocationsAvailable}
        title={tPayment('selectLocation')}
        allowClose={true}
      />

      {/* Out of Stock Confirmation Dialog */}
      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
};
